import { connectorMidpoint, connectorPath, type Point } from '@os/engine';
import { resolveEnd } from './connectors';
import { CONTAINER_TYPES, type ObjectData } from './schema';

export type WorldBox = { x: number; y: number; w: number; h: number; rotation: number };
/**
 * Derived, read-only structure of a board: effective parents (orphans and cycles cut), children in z order, world
 * origins and boxes, paint order (container, then its children) and objects hidden inside collapsed sections.
 */
export type SceneGraph = {
  parent: ReadonlyMap<string, string | null>;
  children: ReadonlyMap<string | null, readonly string[]>;
  /** World position of an object's local origin (what its children's coordinates are relative to). */
  origin: ReadonlyMap<string, Point>;
  world: ReadonlyMap<string, WorldBox>;
  paint: readonly string[];
  rank: ReadonlyMap<string, number>;
  hidden: ReadonlySet<string>;
  /** Connectors' drawing: SVG path, a polyline close to it (hit-testing) and the world points of both ends. */
  paths: ReadonlyMap<string, ConnectorDrawing>;
};
/** `mid`: halfway along the drawing (label, bend handle). */
export type ConnectorDrawing = { d: string; samples: readonly Point[]; start: Point; end: Point; mid: Point };

export const SECTION_COLLAPSED_H = 48;

const isContainer = (o: Readonly<ObjectData> | undefined) => !!o && (CONTAINER_TYPES as readonly string[]).includes(o.type);
const byZ = (a: Readonly<ObjectData>, b: Readonly<ObjectData>) => (a.z < b.z ? -1 : a.z > b.z ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

export function buildGraph(list: ReadonlyArray<Readonly<ObjectData>>): SceneGraph {
  const byId = new Map(list.map((o) => [o.id, o]));
  const parent = new Map<string, string | null>();
  for (const o of list) parent.set(o.id, o.parentId !== null && o.parentId !== o.id && isContainer(byId.get(o.parentId)) ? o.parentId : null);
  // Cycles (forged or imported): cut each loop at its smallest id, so every peer makes the same choice.
  for (const start of [...parent.keys()].sort()) {
    const path: string[] = [];
    let cur: string | null = start;
    while (cur !== null && !path.includes(cur)) {
      path.push(cur);
      cur = parent.get(cur) ?? null;
    }
    if (cur === null) continue;
    parent.set(path.slice(path.indexOf(cur)).reduce((a, b) => (b < a ? b : a)), null);
  }

  const children = new Map<string | null, string[]>();
  for (const o of [...list].sort(byZ)) {
    const p = parent.get(o.id) ?? null;
    let arr = children.get(p);
    if (!arr) children.set(p, (arr = []));
    arr.push(o.id);
  }

  const origin = new Map<string, Point>();
  const world = new Map<string, WorldBox>();
  const paint: string[] = [];
  const hidden = new Set<string>();
  const visit = (id: string, base: Point, hide: boolean) => {
    const o = byId.get(id)!;
    const at = { x: base.x + o.x, y: base.y + o.y };
    origin.set(id, at);
    paint.push(id);
    if (hide) hidden.add(id);
    const container = isContainer(o);
    const collapsed = o.type === 'section' && o.collapsed;
    if (o.type === 'connector') {
      connectors.push(o);
    } else {
      // Containers never rotate (a forged rotation is ignored, children are placed unrotated).
      world.set(id, { x: at.x, y: at.y, w: o.w, h: collapsed ? SECTION_COLLAPSED_H : o.h, rotation: container ? 0 : o.rotation });
    }
    for (const c of children.get(id) ?? []) visit(c, at, hide || collapsed);
    if (o.type === 'group') {
      const kids = descendants(id).filter((d) => byId.get(d)!.type !== 'group');
      if (kids.length) {
        const rects = kids.map((d) => world.get(d)!).map(aabb);
        const minX = Math.min(...rects.map((r) => r.minX));
        const minY = Math.min(...rects.map((r) => r.minY));
        const maxX = Math.max(...rects.map((r) => r.maxX));
        const maxY = Math.max(...rects.map((r) => r.maxY));
        world.set(id, { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY), rotation: 0 });
      }
    }
  };
  const descendants = (id: string): string[] => (children.get(id) ?? []).flatMap((c) => [c, ...descendants(c)]);
  const connectors: Extract<ObjectData, { type: 'connector' }>[] = [];
  for (const id of children.get(null) ?? []) visit(id, { x: 0, y: 0 }, false);

  // Connectors last: their ends sit on the other objects' world boxes.
  const paths = new Map<string, ConnectorDrawing>();
  for (const o of connectors) {
    const a = resolveEnd(o.start, world, hidden, parent);
    const b = resolveEnd(o.end, world, hidden, parent);
    const sides = { a: a.side, b: b.side };
    const path = connectorPath(a.p, b.p, o.routing, sides, o.bend ?? 0);
    paths.set(o.id, { d: path.d, samples: path.samples, start: a.p, end: b.p, mid: connectorMidpoint(a.p, b.p, o.routing, sides, o.bend ?? 0) });
    const r = path.bounds;
    world.set(o.id, { x: r.minX, y: r.minY, w: Math.max(1, r.maxX - r.minX), h: Math.max(1, r.maxY - r.minY), rotation: 0 });
  }

  return { parent, children, origin, world, paint, rank: new Map(paint.map((id, i) => [id, i])), hidden, paths };
}

/** Axis-aligned bounds of a (possibly rotated around its centre) world box. */
export function aabb(b: WorldBox): { minX: number; minY: number; maxX: number; maxY: number } {
  const r = (b.rotation * Math.PI) / 180;
  const hw = (Math.abs(Math.cos(r)) * b.w + Math.abs(Math.sin(r)) * b.h) / 2;
  const hh = (Math.abs(Math.sin(r)) * b.w + Math.abs(Math.cos(r)) * b.h) / 2;
  const cx = b.x + b.w / 2;
  const cy = b.y + b.h / 2;
  return { minX: cx - hw, minY: cy - hh, maxX: cx + hw, maxY: cy + hh };
}

/** Locked itself, or inside a locked container (a locked section locks its whole content). */
export function isEffectivelyLocked(g: SceneGraph, read: (id: string) => Readonly<ObjectData> | undefined, id: string): boolean {
  return !!read(id)?.locked || ancestorsOf(g, id).some((a) => !!read(a)?.locked);
}

export function ancestorsOf(g: SceneGraph, id: string): string[] {
  const out: string[] = [];
  for (let p = g.parent.get(id) ?? null; p !== null; p = g.parent.get(p) ?? null) out.push(p);
  return out;
}

export function descendantsOf(g: SceneGraph, id: string): string[] {
  return (g.children.get(id) ?? []).flatMap((c) => [c, ...descendantsOf(g, c)]);
}

/** The ids whose ancestors are not in `ids` (a selected container already carries its children). */
export function topLevel(g: SceneGraph, ids: readonly string[]): string[] {
  const set = new Set(ids);
  return ids.filter((id) => !ancestorsOf(g, id).some((a) => set.has(a)));
}

/**
 * The open section of highest paint rank whose world box contains `center` (a container: its whole box), never `id`
 * itself nor one of its descendants.
 */
export function dropTarget(g: SceneGraph, list: ReadonlyArray<Readonly<ObjectData>>, id: string, center: Point): string | null {
  const byId = new Map(list.map((o) => [o.id, o]));
  return targetIn(g, byId, openSections(g, byId), id, center, null);
}

/** Open, visible, unlocked sections, topmost first. */
function openSections(g: SceneGraph, byId: ReadonlyMap<string, Readonly<ObjectData>>): string[] {
  return g.paint.filter((sid) => {
    const s = byId.get(sid);
    return s?.type === 'section' && !s.collapsed && !g.hidden.has(sid) && !isEffectivelyLocked(g, (id) => byId.get(id), sid); // a locked section takes no new child
  }).reverse();
}

/** `self`: the object's box where it would be (a moving container), else its stored world box. */
function targetIn(g: SceneGraph, byId: ReadonlyMap<string, Readonly<ObjectData>>, sections: readonly string[], id: string, center: Point, self: Box | null): string | null {
  if (byId.get(id)?.type === 'connector') return null;
  const excluded = new Set([id, ...descendantsOf(g, id)]);
  // A container only lands in a section it fits in entirely (a big section never nests into a small one by its centre).
  const fit = isContainer(byId.get(id)) ? (self ?? g.world.get(id)) : undefined;
  for (const sid of sections) {
    if (excluded.has(sid)) continue;
    const w = g.world.get(sid)!;
    const inside = fit
      ? fit.x >= w.x && fit.y >= w.y && fit.x + fit.w <= w.x + w.w && fit.y + fit.h <= w.y + w.h
      : center.x >= w.x && center.x <= w.x + w.w && center.y >= w.y && center.y <= w.y + w.h;
    if (inside) return sid;
  }
  return null;
}

type Box = { x: number; y: number; w: number; h: number };
export type DropMove = { id: string; from: string | null; to: string | null };

/**
 * Where each moved object would land given its world box (`boxOf`, the live one during a drag): the section under its
 * centre, or the board. Connectors, children of a group and objects carried by a moving ancestor are left out. The
 * same answer drives the drag highlight and the commit.
 */
export function dropPlan(g: SceneGraph, list: ReadonlyArray<Readonly<ObjectData>>, ids: readonly string[], boxOf: (id: string) => Box | null): DropMove[] {
  // Runs at every pointer move of a drag: the sections and the moving set are computed once.
  const byId = new Map(list.map((o) => [o.id, o]));
  const sections = openSections(g, byId);
  const moving = new Set(ids);
  const out: DropMove[] = [];
  for (const id of ids) {
    const b = boxOf(id);
    if (!b || byId.get(id)?.type === 'connector' || ancestorsOf(g, id).some((a) => moving.has(a))) continue;
    const from = g.parent.get(id) ?? null;
    if (from !== null && byId.get(from)?.type === 'group') continue;
    out.push({ id, from, to: targetIn(g, byId, sections, id, { x: b.x + b.w / 2, y: b.y + b.h / 2 }, b) });
  }
  return out;
}
